import { useRef, useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import { supabase, uploadProductImage } from '../supabaseClient';
import { cache, whatsapp, formatKES, CACHE_CONFIG } from '../utils/cacheUtils';
import heroIllustration from '../assets/hero-illustration.png';
import { useAdmin } from '../hooks/useAdmin';
import Modal from '../components/Modal';
import CacheManager from '../components/CacheManager';
import SEO from '../components/SEO';

const PERSON_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Person',
  name: 'Joan Christine Nyawira',
  alternateName: "Jowey's",
  jobTitle: ['Physiotherapist', 'Digital Marketer', 'Academic Writer'],
  url: 'https://joweyluxe.com/',
  image: 'https://joweyluxe.com/logo512.png',
  email: 'mailto:muriithijoanchristine@gmail.com',
  address: { '@type': 'PostalAddress', addressCountry: 'KE' },
  sameAs: [],
};

export default function Shop() {
  const location = useLocation();
  const productsRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', price: '' });
  const [imageFile, setImageFile] = useState(null);
  const [posting, setPosting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cacheManagerOpen, setCacheManagerOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [displayCount, setDisplayCount] = useState(0);

  const { adminMode } = useAdmin();

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchProducts = async (forceRefresh = false) => {
    setLoading(true);
    if (!forceRefresh) {
      const cached = cache.get(CACHE_CONFIG.PRODUCTS);
      if (cached) {
        setProducts(cached.data);
        setLoading(false);
        if (cached.isStale) fetchProducts(true);
        return;
      }
    }
    try {
      const { data, error } = await supabase.from('products').select('*').order('id', { ascending: false });
      if (error) throw error;
      setProducts(data || []);
      cache.set(CACHE_CONFIG.PRODUCTS, data || []);
    } catch {
      setProducts([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchProducts(); }, []);

  useEffect(() => {
    if (products.length === 0) {
      setDisplayCount(0);
      return;
    }
    const target = products.length;
    const duration = 900;
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayCount(Math.round(target * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [products.length]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productId = params.get('product');
    if (productId && products.length > 0) {
      const product = products.find((p) => p.id.toString() === productId);
      if (product) {
        setSelectedProduct(product);
        productsRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
    if (params.get('search')) {
      productsRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.search, products]);

  const getFilteredProducts = () => {
    const q = new URLSearchParams(location.search).get('search');
    if (!q) return products;
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q.toLowerCase()) ||
        p.description.toLowerCase().includes(q.toLowerCase())
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      if (!imageFile) throw new Error('Please select an image');
      const imageUrl = await uploadProductImage(imageFile);
      const { error } = await supabase.from('products').insert([{
        name: form.name,
        description: form.description,
        price: Number(form.price),
        image_url: imageUrl,
      }]);
      if (error) throw error;
      cache.clear(CACHE_CONFIG.PRODUCTS);
      await fetchProducts(true);
      setForm({ name: '', description: '', price: '' });
      setImageFile(null);
      setAdminModalOpen(false);
      showToast('Product added!');
    } catch (err) {
      showToast(err.message || 'Failed to add product', true);
    }
    setPosting(false);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this product?')) return;
    const { error } = await supabase.from('products').delete().eq('id', id);
    if (error) showToast('Failed to delete', true);
    else {
      setProducts((prev) => prev.filter((p) => p.id !== id));
      cache.clear(CACHE_CONFIG.PRODUCTS);
    }
  };

  const searchQuery = new URLSearchParams(location.search).get('search');
  const filtered = getFilteredProducts();

  return (
    <>
      <SEO
        title="Jowey's | Crochet Shop, Physiotherapy & Digital Marketing — Joan Christine Nyawira"
        description="Shop curated crochet pieces and luxe finds from Jowey's collection, and meet Joan Christine Nyawira — physiotherapist, digital marketer, and blogger based in Kenya."
        path="/"
        jsonLd={PERSON_JSON_LD}
      />
      {/* Hero */}
      <section className="hero container">
        <div>
          <div className="hero-badge">Joweyluxe</div>
          <h1><span className="hero-word">Shine</span> <em className="hero-word">on</em></h1>
          <p className="hero-sub">
            Beauty that reflects your spirit — curated crochet pieces and luxe finds from Jowey's collection.
          </p>
          <div className="hero-actions">
            <button className="btn btn-primary" onClick={() => productsRef.current?.scrollIntoView({ behavior: 'smooth' })}>
              Shop Now
            </button>
            <a href="/about" className="btn btn-ghost">About Jowey</a>
          </div>
        </div>
        <div className="hero-visual">
          <span className="floaty floaty-1" aria-hidden="true">✦</span>
          <span className="floaty floaty-2" aria-hidden="true">♥</span>
          <span className="floaty floaty-3" aria-hidden="true">✦</span>
          <div className="hero-image-wrap">
            <div className="hero-image-float">
              <img src={heroIllustration} alt="Jowey's illustration" />
            </div>
          </div>
          <div className="hero-float">
            <div className="hero-float-label">Collection</div>
            <div className="hero-float-value">{displayCount} pieces</div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="section container" ref={productsRef}>
        <div className="section-header">
          <div>
            <div className="section-label">Shop</div>
            <h2 className="section-title">Products</h2>
          </div>
          {adminMode && (
            <div className="admin-bar">
              <button className="btn btn-ghost btn-sm" onClick={() => setAdminModalOpen(true)}>+ Add Product</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setCacheManagerOpen(true)}>Cache</button>
            </div>
          )}
        </div>

        {searchQuery && (
          <div className="search-banner">
            Showing <strong>{filtered.length}</strong> result{filtered.length !== 1 ? 's' : ''} for &ldquo;<strong>{searchQuery}</strong>&rdquo;
          </div>
        )}

        {loading ? (
          <div className="loading-state">Loading products…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">No products yet. Check back soon!</div>
        ) : (
          <div className="product-grid">
            {filtered.map((product, i) => (
              <article
                key={product.id}
                className="product-card"
                style={{ animationDelay: `${Math.min(i, 10) * 60}ms` }}
                onClick={() => setSelectedProduct(product)}
              >
                <div className="product-card-img">
                  <img src={product.image_url || product.imageUrl} alt={product.name} loading="lazy" />
                </div>
                <div className="product-card-body">
                  <h3 className="product-card-title">
                    <Link to={`/shop/${product.id}`} onClick={(e) => e.stopPropagation()}>{product.name}</Link>
                  </h3>
                  <p className="product-card-desc">{product.description}</p>
                  <div className="product-card-footer">
                    <span className="product-price">{formatKES(product.price)}</span>
                    <button
                      className="btn btn-whatsapp btn-sm"
                      onClick={(e) => { e.stopPropagation(); whatsapp.openWhatsApp(product); }}
                    >
                      <FaWhatsapp /> Order
                    </button>
                  </div>
                  {adminMode && (
                    <button
                      className="btn btn-danger btn-sm"
                      style={{ marginTop: '0.75rem', width: '100%' }}
                      onClick={(e) => handleDelete(product.id, e)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Add product modal */}
      <Modal open={adminMode && adminModalOpen} onClose={() => setAdminModalOpen(false)}>
        <h3 style={{ fontFamily: 'Syne', marginBottom: '1.25rem' }}>Add Product</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input className="form-input" name="name" placeholder="Product name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="form-group">
            <textarea className="form-input" name="description" placeholder="Description" rows={3}
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
          </div>
          <div className="form-group">
            <input className="form-input" type="number" name="price" placeholder="Price (KES)" min="0" step="0.01"
              value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
          </div>
          <div className="form-group">
            <input className="form-input" type="file" accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])} required />
          </div>
          <button type="submit" className="btn btn-primary" disabled={posting} style={{ width: '100%' }}>
            {posting ? 'Adding…' : 'Add Product'}
          </button>
        </form>
      </Modal>

      {/* Product detail */}
      <Modal open={!!selectedProduct} onClose={() => setSelectedProduct(null)} large>
        {selectedProduct && (
          <>
            <img
              className="product-detail-img"
              src={selectedProduct.image_url || selectedProduct.imageUrl}
              alt={selectedProduct.name}
            />
            <h2 className="product-detail-title">{selectedProduct.name}</h2>
            <p className="product-detail-desc">{selectedProduct.description}</p>
            <div className="product-detail-price">{formatKES(selectedProduct.price)}</div>
            <button className="btn btn-whatsapp" onClick={() => whatsapp.openWhatsApp(selectedProduct)}>
              <FaWhatsapp /> Order via WhatsApp
            </button>
          </>
        )}
      </Modal>

      <CacheManager open={cacheManagerOpen} onClose={() => setCacheManagerOpen(false)} />

      {toast && (
        <div className={`toast${toast.isError ? ' toast-error' : ''}`}>{toast.msg}</div>
      )}
    </>
  );
}
